<template>
  <v-sheet
    color="white"
    elevation="5"
    rounded
    class="mt-5 mb-5 pt-3 pb-5 pl-3 pr-3"
    height="100%"
  >
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      ></v-progress-circular>
    </v-overlay>
    <div>
      <v-row class="ma-0 mb-3">
        <h2>{{title}}</h2>
        <v-btn class="ml-2" color="primary" v-on:click="createNewLitter">+ New Litter</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" v-on:click="applyFilter">Apply Filters</v-btn
        >&nbsp;
        <v-btn color="secondary" v-on:click="clearAllFilters">Clear</v-btn>
      </v-row>
    </div>
    <PaginationSelect
      :totalCount="paginatedResponse.totalCount"
      :page="options.page"
      :size="options.itemsPerPage"
      v-on:load="load"
      v-on:changePage="options.page = $event"
      v-on:changeSize="options.itemsPerPage = $event"
    />
    <v-row>
      <v-alert width="100%" class="mr-5 ml-5" v-model="isError" dismissible type="error"> {{ errorMessage }}</v-alert>
    </v-row>
    <v-data-table
      dense
      disable-sort
      hide-default-footer
      class="elevation-1"
      :loading="loading"
      :headers="headers"
      :options.sync="options"
      :items="paginatedResponse.data"
      :server-items-length="paginatedResponse.totalCount"
      :footer-props="footerProps">
      <template v-slot:header.litterId="{ header }">
        <v-text-field
          v-model="searchParams.litterId"
          label="Litter ID"
          clearable
          @click:clear="searchParams.litterId = ''"
          v-on:keyup.enter="applyFilter"
        />
      </template>
      <template v-slot:header.damId="{ header }">
        <v-text-field
          hide-details
          v-model="searchParams.damId"
          label="Dam ID"
          clearable
          @click:clear="searchParams.damId = ''"
          v-on:keyup.enter="applyFilter"
        />&nbsp;
        <v-text-field
          hide-details
          class="mt-0 pt-0 mb-2"
          v-model="searchParams.damName"
          label="Dam Name"
          clearable
          @click:clear="searchParams.damName = ''"
          v-on:keyup.enter="applyFilter"
        />
      </template>
      <template v-slot:header.sireId="{ header }">
        <v-text-field
          hide-details
          v-model="searchParams.sireId"
          label="Sire ID"
          clearable
          @click:clear="searchParams.sireId = ''"
          v-on:keyup.enter="applyFilter"
        />&nbsp;
        <v-text-field
          hide-details
          class="mt-0 pt-0 mb-2"
          v-model="searchParams.sireName"
          label="Sire Name"
          clearable
          @click:clear="searchParams.sireName = ''"
          v-on:keyup.enter="applyFilter"
        />
      </template>
      <template v-slot:header.owners="{ header }">
        <div>
          <v-text-field
            v-model="searchParams.ownerName"
            label="Owner/Co-Owner Name"
            clearable
            @click:clear="searchParams.ownerName = ''"
          v-on:keyup.enter="applyFilter"
          />
          <v-text-field
            v-model="searchParams.memberId"
            label="Member ID"
            clearable
            @click:clear="searchParams.memberId = ''"
          v-on:keyup.enter="applyFilter"
          />
        </div>
      </template>
      <template v-slot:header.whelpingDate="{ header }">
        <div width="300px">
          <datepicker 
            :date="searchParams.whelpingDateFrom" 
            label="Whelping Date From" 
            v-on:updateDates="updateDates($event, 'whelpingDateFrom')"
            v-on:enter="updateDateSearch($event, 'whelpingDateFrom')"
          />
          <datepicker
            :date="searchParams.whelpingDateTo"
            label="Whelping Date To"
            v-on:updateDates="updateDates($event, 'whelpingDateTo')"
            v-on:enter="updateDateSearch($event, 'whelpingDateTo')"
          />
        </div>
      </template>
      <template v-slot:item.litterId="{ item }">
        <router-link :to="`/admin/litters/${item.litterId}`" target="_blank">{{
          item.litterId
        }}</router-link>
      </template>
      <template v-slot:item.whelpingDate="{ item }">
        <localized-date :date="item.whelpingDate" />
      </template>
      <template v-slot:item.damId="{ item }">
        <span> {{ item.damName }} - {{ item.damId }} </span>
      </template>
      <template v-slot:item.sireId="{ item }">
        <span> {{ item.sireName }} - {{ item.sireId }} </span>
      </template>
      <template v-slot:item.owners="{ item }">
        <div class="pl-5">
          <CoownersList
            :owners="item.owners"
            :ownershipType="litterOwnershipType"
            :hideHeader="true"
          />
        </div>
      </template>
      <template v-slot:item.registrationId="{ item }">
        <p class="mt-3">
          <a
            v-if="!!item.registrationId"
            v-on:click="showRegistration(item.registrationId)"
            >View</a
          >
          <br />
        </p>
      </template>
      <template v-slot:item.registrationDate="{ item }">
        <localized-date :date="item.registrationDate" />
      </template>
    </v-data-table>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import CoownersList, { OwnershipType } from "@/components/common/CoownersList.vue";
import Datepicker from "@/components/common/inputs/DatepickerFormatted.vue";
import PaginationSelect from "@/components/common/PaginationSelect.vue";

import litterService from "@/services/admin/litterService";
import { getErrorMessage } from "@/services/errorHandler";

import { MetaNames } from "@/constants";
import { UserInfo } from "@/types/User";
import { PaginationOptions, PaginatedResponse } from "@/types/Pagination";

type WhelpingDateFilter = "whelpingDateFrom" | "whelpingDateTo";

export default Vue.extend({
  name: "LitterList",
  components: {
    CoownersList,
    Datepicker,
    PaginationSelect
  },
  data: function () {
    return {
      title: MetaNames.AdminLitters,
      loading: false,
      isError: false,
      errorMessage: "",
      paginatedResponse: {} as PaginatedResponse<object>,
      searchParams: {
        damId: "",
        damName: "",
        sireId: "",
        sireName: "",
        ascending: false,
        ownerName: "",
        memberId: "",
        whelpingDateFrom: null as Temporal.PlainDate | null,
        whelpingDateTo: null as Temporal.PlainDate | null,
      },
      options: {} as PaginationOptions,
      headers: [
        { text: "", align: "left", value: "litterId", width: "100px" },
        { text: "", align: "left", value: "damId", width: "200px" },
        { text: "", align: "left", value: "sireId", width: "200px" },
        { text: "", align: "center", value: "whelpingDate", width: "175px" },
        { text: "", align: "left", value: "owners", width: "200px" },
        {
          text: "Registration",
          align: "left",
          value: "registrationId",
          width: "75px",
        },
        {
          text: "Registration date",
          align: "center",
          value: "registrationDate",
          width: "100px",
        },
      ],
      footerProps: { 'items-per-page-options': [10, 20, 30, 40, 50, 100] },
    };
  },
  created: function () {
    this.load();
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    }
  },
  methods: {
    createNewLitter: function() {
      this.$router.push({path: '/admin/litters'});
    },
    clearAllFilters: function () {
      this.options.page = 1;
      this.options.itemsPerPage = 10;
      this.searchParams = {
        damId: "",
        damName: "",
        sireId: "",
        sireName: "",
        ascending: false,
        ownerName: "",
        memberId: "",
        whelpingDateFrom: null,
        whelpingDateTo: null,
      };
    },
    updateDates: function (val: { date: Temporal.PlainDate | null }, key: WhelpingDateFilter) {
      this.searchParams[key] = val.date;
    },
    updateDateSearch: async function(val: { date: Temporal.PlainDate | null }, key: WhelpingDateFilter) {
      await this.updateDates(val, key);
      this.applyFilter();
    },
    showRegistration: function (id: any, _: any) {
      this.$router.push({ path: `/admin/registration/litters/${id}` });
    },
    load: async function () {
      this.loading = true;

      const {
        page,
        itemsPerPage,
      } = this.options;

      try {
        const response = await litterService.getLittersList({
          ...this.options,
          page: (page ?? 1) - 1,
          itemsPerPage: itemsPerPage ?? 10
        }, this.searchParams);

        this.loading = false;
        this.paginatedResponse = response;
      } catch (ex: any) {
        this.loading = false;
        this.isError = true;

        this.errorMessage = getErrorMessage(ex);
      }
    },
    applyFilter() {
      this.options.page = 1;
      this.options.itemsPerPage = 10;

      this.load();
    },
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    dogOwnershipType: function () {
      return OwnershipType.Dog;
    },
    litterOwnershipType: function () {
      return OwnershipType.Litter;
    },
  },
});
</script>
